import {
  SkyCard,
  SkyDataGrid,
  SkyDropdown,
  SkyText,
  type SkyDataGridColumn,
} from "@skypointcloud/skypoint-ui";
import type { ReactNode } from "react";
import type { ConnectorAdminView } from "@shared/types/connectorsAdmin";
import type { InstanceAdminView } from "@shared/types/instancesAdmin";
import { ErrorState, GridSkeleton } from "@/components/platform/ScreenState";
import { RefreshErrorBar } from "@/components/states/RefreshErrorBar";
import {
  useClearConnectorPin,
  useConnectorPin,
  useInstancesAdmin,
  useSetConnectorPin,
} from "@/hooks/use-instances-admin";
import { useNotice } from "@/lib/feedback/NoticeProvider";
import { useApiErrorNotice } from "@/lib/feedback/useApiErrorNotice";
import { formatInstant } from "@/lib/time/displayTime";
import { useDisplayTz } from "@/hooks/use-display-tz";

/**
 * Per-instance image tag pins for one connector (FR-32, canvas 13 panel 04).
 *
 * An instance runs the definition's default tag unless it is pinned here. Two
 * reasons to pin, and the copy names both: hold one instance back on an older
 * image, or try a new one on a single instance before it becomes the default.
 *
 * "Not pinned" is a 404 from the pin route, which is the right answer to the
 * question and the wrong thing to show as a failure. `useConnectorPin`
 * translates it to null once, so every row here can treat absence as a value.
 */

const USE_DEFAULT = "__default__";

function PinCell({
  instance,
  connector,
}: {
  instance: InstanceAdminView;
  connector: ConnectorAdminView;
}) {
  const notice = useNotice();
  const showError = useApiErrorNotice();
  const pin = useConnectorPin(instance.id, connector.name);
  const setPin = useSetConnectorPin(connector.name);
  const clearPin = useClearConnectorPin(connector.name);

  const options = [
    { value: USE_DEFAULT, text: "Use default" },
    ...connector.versions.map((version) => ({
      value: version.tag,
      text: version.isDefault ? `${version.tag} (default)` : version.tag,
    })),
  ];

  const selected = pin.data?.tag ?? USE_DEFAULT;

  return (
    <SkyDropdown
      options={options}
      selectedOptions={[selected]}
      loading={pin.isPending || setPin.isPending || clearPin.isPending}
      aria-label={`Pinned tag for ${instance.name}`}
      testId={`pin-${instance.slug}`}
      onOptionSelect={(values) => {
        const next = values[0] ?? USE_DEFAULT;
        if (next === selected) return;
        if (next === USE_DEFAULT) {
          clearPin.mutate(
            { instanceId: instance.id },
            {
              onSuccess: () => notice.success(`${instance.name} runs the default tag`),
              onError: (error) => showError(error, "That pin was not cleared."),
            },
          );
          return;
        }
        setPin.mutate(
          { instanceId: instance.id, tag: next },
          {
            onSuccess: () => notice.success(`${instance.name} pinned to ${next}`),
            onError: (error) => showError(error, "That pin was not saved."),
          },
        );
      }}
    />
  );
}

function RunsOnCell({
  instance,
  connector,
}: {
  instance: InstanceAdminView;
  connector: ConnectorAdminView;
}) {
  const zone = useDisplayTz();
  const pin = useConnectorPin(instance.id, connector.name);
  const defaultTag = connector.versions.find((version) => version.isDefault)?.tag ?? "none";

  return (
    <div className="flex flex-col">
      <SkyText size={300} font="monospace" block>
        {pin.data?.tag ?? defaultTag}
      </SkyText>
      <SkyText size={200} color="muted" block>
        {pin.data ? `pinned ${formatInstant(pin.data.updatedAt, zone, "day")}` : "default"}
      </SkyText>
    </div>
  );
}

export function ConnectorPinsCard({ connector }: { connector: ConnectorAdminView }) {
  const instances = useInstancesAdmin();

  const columns: SkyDataGridColumn<InstanceAdminView>[] = [
    {
      // The tenant once, under the instance (round 6 A3: the separate Tenant column repeated it),
      // by its Display name.
      key: "name",
      header: "Instance",
      width: "40%",
      renderCell: (_value, row) => (
        <div className="flex flex-col">
          <SkyText size={300} weight="medium" block>
            {row.name}
          </SkyText>
          <SkyText size={200} color="muted" block>
            {row.tenantDisplayName}
          </SkyText>
        </div>
      ),
    },
    {
      key: "runsOn",
      header: "Runs on",
      width: "30%",
      renderCell: (_value, row) => <RunsOnCell instance={row} connector={connector} />,
    },
    {
      key: "pinned",
      header: "Pinned",
      width: "30%",
      renderCell: (_value, row) => <PinCell instance={row} connector={connector} />,
    },
  ];

  const rows = (instances.data ?? []).filter((instance) => instance.status !== "disabled");

  function renderPins(): ReactNode {
    if (instances.isPending) {
      return <GridSkeleton rows={3} label="Loading instances…" />;
    }
    // #1398: only a failed first read replaces the list; a failed re-read keeps the rows.
    if (instances.isError && !instances.data) {
      return (
        <ErrorState
          title="The instance list did not load"
          error={instances.error}
          onRetry={() => void instances.refetch()}
        />
      );
    }
    if (rows.length === 0) {
      return (
        <SkyText size={200} color="muted" block>
          This deployment has no active instances to pin.
        </SkyText>
      );
    }
    return (
      <>
        {instances.isError ? (
          <RefreshErrorBar
            things="instances"
            error={instances.error}
            onRetry={() => void instances.refetch()}
            testId="connector-pins-refresh-error"
          />
        ) : null}
        <SkyDataGrid<InstanceAdminView>
          data={rows}
          columns={columns}
          getRowId={(row) => row.id}
          testId="connector-pins-grid"
        />
      </>
    );
  }

  return (
    <SkyCard appearance="outline" testId="connector-pins-card">
      <div className="flex flex-col gap-3 p-1">
        <div>
          <SkyText as="h2" size={400} weight="semibold" block>
            Pinned image tags for {connector.name}
          </SkyText>
          <SkyText size={200} color="muted" block>
            An instance runs the default tag unless it is pinned here. Pin to hold an instance back,
            or to try a new tag on one instance first.
          </SkyText>
        </div>

        {renderPins()}
      </div>
    </SkyCard>
  );
}
